import { t } from 'i18n';
import React, {
  createContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { styled, Checkbox, SvgIcon } from '@teko/ui';
import { TermAndPolicyText } from '@teko/ecommerce';
import get from 'lodash.get';
import { SUBSCRIBE, CONFIRM_UNSUBSCRIBE } from '../constants';
import { useGlobalData } from '../contexts';
import { useAuth } from '@teko/auth-react';
import { mobioService } from '../services';
interface ProfileTag {
  id: string;
}
interface MobioContextValue {
  mobioProfile: any;
  updateTags: (tags: ProfileTag[]) => void;
}
const defaultValues: MobioContextValue = {
  mobioProfile: {},
  updateTags: (_) => _,
};
export const MobioContext = createContext(defaultValues);
interface MobioContextProps {
  children?: React.ReactNode;
}
interface ModalProps {
  showAnimation: boolean;
  startAnimation: boolean;
  isMobile: boolean;
}
const Modal = styled.div<ModalProps>(
  ({ showAnimation, startAnimation, isMobile }) => `
    background-color: white;
    position: fixed;
    ${
      !isMobile
        ? 'left: 2vw;'
        : `
          transform: translate(-50%, -50%);
          left: 50%;
        `
    }
    padding: 1rem;
    box-shadow: 5px 5px 20px 1px rgba(0, 0, 0, 0.1);
    width: 21rem;
    border-radius: 3px;
    z-index: 10000;
    visibility: ${startAnimation ? 'initial' : 'hidden'};
    opacity: ${showAnimation ? '1' : '0'};
    animation: ${showAnimation ? 'slideUp' : 'slideDown'};
    bottom: ${showAnimation ? '5vh' : '-20vh'};
    animation-duration: 0.3s;
    animation-timing-function: ease-in-out;
    animation-iteration-count: 1;

    @keyframes slideUp {
      0% {
        opacity: 0;
        bottom: -20vh;
      }
      100% {
        opacity: 1;
        bottom: 5vh;
      }
    } 

    @keyframes slideDown {
      0% {
        opacity: 1;
        bottom: 5vh;
      }
      100% {
        opacity: 0;
        bottom: -20vh;
      }
    }
  `,
);
const ModalHeader = styled.p`
  font-size: 0.9375rem;
  line-height: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: #434657;
`;
const ModalDescription = styled.p`
  font-size: 0.8125rem;
  color: #82869e;
  margin-bottom: 1rem;
  line-height: 1.125rem;
  font-weight: 400;
`;
const ModalFooter = styled.div`
  display: flex;
  align-items: center;
`;
const StyledCheckbox = styled(Checkbox)`
  flex-shrink: 0;
  margin-top: 3px;
  border-radius: 2px;
`;
const Text = styled.p`
  margin: 0;
  margin-left: 0.5rem;
  cursor: pointer;
  user-select: none;
  line-height: 1.125rem;
  font-weight: 400;
  font-size: 0.8125rem;
  color: #434657;
`;
const StyledSvgIcon = styled(SvgIcon)`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  cursor: pointer;
`;
const TOTAL_COUNT_DOWN = 5000;
export const MobioContextProvider: React.FunctionComponent<
  MobioContextProps
> = ({ children }: MobioContextProps) => {
  const { user: myProfile } = useAuth();
  const [isSubscribe, setIsSubscribe] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [startShowModal, setStartShowModal] = useState(false);
  const [mobioProfile, setMobioProfile] = useState({});
  const [isTimeout, setIsTimeout] = useState(false);
  const { isMobile } = useGlobalData();
  const updateTags = useCallback((profileTags: ProfileTag[]) => {
    setMobioProfile((prev) => ({ ...prev, profile_tags: profileTags }));
  }, []);
  const customerId = useMemo(() => get(mobioProfile, 'id'), [mobioProfile]);
  const subscribe = useCallback(
    (customerId: string) => {
      if (customerId) {
        const subscribeTags = SUBSCRIBE.reduce(
          (total: string[], { options }) => [
            ...total,
            ...options.map((option) => option.id),
          ],
          [],
        );
        const unsubscribeTags = CONFIRM_UNSUBSCRIBE.reduce(
          (total: string[], { options }) => [
            ...total,
            ...options.map((option) => option.id),
          ],
          [],
        );
        mobioService
          .updateMobioTags(customerId, subscribeTags, unsubscribeTags)
          .then(({ data }) =>
            updateTags(get(data, 'data.profile_info.profile_tags') || []),
          );
      }
    },
    [updateTags],
  );
  useEffect(() => {
    (async () => {
      if (myProfile) {
        const alternateEmail =
          !myProfile.email && !myProfile.telephone
            ? `${myProfile.userId}@teko.vn`
            : '';
        const mobioProfileResponse = await mobioService.getProfile(
          myProfile.telephone || myProfile.email || alternateEmail,
        );
        if (get(mobioProfileResponse, 'errors')) return;
        const profile = get(mobioProfileResponse, 'data.customers.0');
        if (profile) {
          setMobioProfile(profile);
          return;
        }
        const createMobioProfileResponse =
          await mobioService.createMobioProfile(
            myProfile.name || '',
            myProfile.telephone || '',
            myProfile.email || alternateEmail,
          );
        setMobioProfile(get(createMobioProfileResponse, 'data.profile_info'));
        const time = setTimeout(() => {
          setIsTimeout(true);
          clearTimeout(time);
        }, TOTAL_COUNT_DOWN);
        setStartShowModal(true);
        setShowModal(true);
      }
    })();
  }, [myProfile]);
  useEffect(() => {
    if (isTimeout) {
      if (isSubscribe) {
        subscribe(customerId);
      }
      setShowModal(false);
    }
  }, [isTimeout, isSubscribe, customerId, subscribe]);
  const handleClick = () => {
    setIsSubscribe((prev) => !prev);
  };
  return (
    <MobioContext.Provider value={{ mobioProfile, updateTags }}>
      {children}
      <Modal
        showAnimation={showModal}
        startAnimation={startShowModal}
        isMobile={isMobile}
      >
        <StyledSvgIcon
          name="close"
          onClick={() => setShowModal(false)}
          size={12}
          color="#adadad"
        />
        {!isMobile && (
          <>
            <ModalHeader>{t('Nhận thông tin khuyến mãi')}</ModalHeader>
            <ModalDescription>{t('MobioContext.0')}</ModalDescription>
          </>
        )}
        <ModalFooter>
          <StyledCheckbox
            width="1rem"
            height="1rem"
            checked={isSubscribe}
            onClick={handleClick}
          />
          <Text onClick={handleClick}>
            {t('Tôi đồng ý với')}
            <TermAndPolicyText />
            {t('của Phong Vũ')}
          </Text>
        </ModalFooter>
      </Modal>
    </MobioContext.Provider>
  );
};
